import { z } from 'zod';
import { interviewSchema, interviewStateSchema, jobProfileSchema } from './interview.js';
import { storedTelemetryEventSchema } from './telemetry.js';
import { userIdSchema, userSchema } from './user.js';
/** Minutes before `scheduledAt` that the lobby opens for both roles (design §5). */
export const JOIN_WINDOW_LEAD_MINUTES = 15;
/** Hours around *actual* session end for which a candidate account stays usable (design §6). */
export const CANDIDATE_ACCOUNT_WINDOW_HOURS = 72;
/** Months telemetry and candidate PII are retained after the interview ends (design §6). */
export const RETENTION_MONTHS = 6;
// --- Job profiles (admin CRUD) -------------------------------------------------
export const jobProfileInputSchema = jobProfileSchema.omit({ id: true });
export const jobProfileUpdateSchema = jobProfileInputSchema.partial();
// --- Interviews (admin) --------------------------------------------------------
/**
 * Candidates are addressed by email so an admin can invite someone with no account yet;
 * the API creates the `invited` user on demand. Interviewers must already exist.
 */
export const createInterviewRequestSchema = z.object({
    candidateEmail: z.email(),
    candidateFullName: z.string().min(1),
    interviewerIds: z.array(userIdSchema).min(1).max(4),
    jobProfileId: z.uuid(),
    scheduledAt: z.iso.datetime(),
});
/**
 * Moving an interview to a new slot.
 *
 * Only `scheduledAt` is reschedulable. Changing the job profile or the participants is a
 * different interview wearing the same id — the evidence log, the room and the invitations
 * are all anchored to this row, so those edits would silently rewrite the meaning of
 * history rather than move an appointment.
 */
export const rescheduleInterviewRequestSchema = z.object({
    scheduledAt: z.iso.datetime(),
});
/**
 * Cancelling an interview.
 *
 * The reason is optional and never shown to the candidate: it exists for the audit row, so
 * an admin looking at a cancelled booking months later can see why. Putting it in the
 * candidate's email would turn an administrative note into a message nobody wrote for them.
 */
export const cancelInterviewRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
/** An interview plus the resolved participant records the dashboards render. */
export const interviewDetailSchema = interviewSchema.extend({
    candidate: userSchema,
    interviewers: z.array(userSchema),
});
export const interviewListQuerySchema = z.object({
    state: interviewStateSchema.optional(),
});
// --- Invitations ---------------------------------------------------------------
export const invitationSchema = z.object({
    id: z.uuid(),
    userId: userIdSchema,
    /** Null for interviewer-account invites, which are not tied to a single interview. */
    interviewId: z.uuid().nullable(),
    expiresAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
    usedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const createInterviewerRequestSchema = z.object({
    email: z.email(),
    fullName: z.string().min(1),
});
// --- Join tokens ---------------------------------------------------------------
/** A minted LiveKit token plus the context the session UI needs to connect. */
export const joinTokenResponseSchema = z.object({
    token: z.string().min(1),
    livekitUrl: z.url(),
    roomName: z.string().min(1),
    interviewId: z.uuid(),
    /** The web VSCode surface for this interview's job profile. */
    vscodeUrl: z.url(),
    expiresAt: z.iso.datetime(),
});
// --- Realtime handshake tickets -------------------------------------------------
export const realtimeTicketRequestSchema = z.object({
    interviewId: z.uuid(),
});
/**
 * A single-use, ~60s credential for the WebSocket handshake.
 *
 * Browsers cannot set headers on a WS upgrade, so *something* has to travel in the query
 * string, and whatever does will be written to the access log. This exists so that thing is
 * not the access JWT: a ticket is opaque, bound to one user and one interview, spent on
 * first use, and expired within the minute.
 */
export const realtimeTicketResponseSchema = z.object({
    ticket: z.string().min(1),
    expiresAt: z.iso.datetime(),
});
// --- Telemetry export ----------------------------------------------------------
export const telemetryExportFormatSchema = z.enum(['json', 'csv']);
export const telemetryLogQuerySchema = z.object({
    format: telemetryExportFormatSchema.default('json'),
});
export const telemetryLogResponseSchema = z.object({
    interviewId: z.uuid(),
    events: z.array(storedTelemetryEventSchema),
});
// --- Candidate portal ----------------------------------------------------------
/** The candidate portal renders the profile and, when one exists, the next interview. */
export const candidateProfileResponseSchema = z.object({
    user: userSchema,
    upcomingInterview: interviewDetailSchema.nullable(),
});
// --- Errors --------------------------------------------------------------------
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.enum([
            'bad_request',
            'unauthorized',
            'forbidden',
            'not_found',
            'conflict',
            'join_window_closed',
            'invalid_invitation',
            /** Too many attempts against a throttled endpoint; carries `Retry-After`. */
            'rate_limited',
            'internal',
        ]),
        message: z.string().min(1),
    }),
});
//# sourceMappingURL=api.js.map